import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Eye, X, Loader2 } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";

// Reuses the existing /api/documents infrastructure (upload + secure blob download).
const DocumentField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/documents", fd);
      onChange(data.id);
      setName(data.original_filename);
      toast.success("Receipt attached");
    } catch (err) {
      toast.error("Upload failed");
    }
    setBusy(false);
    e.target.value = "";
  };
  const view = async () => {
    try {
      const res = await api.get(`/documents/${value}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open document");
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {value ? (
        <>
          <span className="flex items-center gap-1 text-xs text-emerald-300"><Paperclip className="h-3.5 w-3.5" /> {name || "Receipt attached"}</span>
          <Button type="button" variant="outline" size="sm" className="h-7 border-[#2D3F62] bg-transparent text-xs text-cyan-300 hover:bg-[#17233B]" onClick={view} data-testid={`${testId}-view`}><Eye className="mr-1 h-3 w-3" /> View</Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-rose-400" onClick={() => { onChange(null); setName(""); }} data-testid={`${testId}-remove`}><X className="mr-1 h-3 w-3" /> Remove</Button>
        </>
      ) : (
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-[#2D3F62] bg-[#0B1120] px-3 py-2 text-xs text-slate-300 hover:bg-[#17233B]" data-testid={`${testId}-attach`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach Receipt / Invoice
          <input type="file" className="hidden" onChange={onPick} accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.docx,.doc" />
        </label>
      )}
    </div>
  );
};

export const FormDialog = ({ open, onOpenChange, title, fields, initial, onSubmit, submitLabel = "Save", testId }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => {
        base[f.name] = initial?.[f.name] ?? f.default ?? (f.type === "switch" ? false : f.type === "number" ? "" : "");
      });
      setValues(base);
    }
  }, [open, initial]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      let v = values[f.name];
      if (v === "__none__") v = null;              // sentinel from optional selects
      if (f.type === "number") v = v === "" || v === null ? 0 : Number(v);
      payload[f.name] = v;
    });
    const ok = await onSubmit(payload);
    setSaving(false);
    if (ok !== false) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-2xl" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-slate-50">{title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.name} className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
              {f.type !== "switch" && <Label className="text-xs text-slate-400">{f.label}</Label>}
              {f.type === "select" ? (
                <Select value={String(values[f.name] ?? "")} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className={inputCls} data-testid={`field-${f.name}`}>
                    <SelectValue placeholder={f.placeholder || "Select"} />
                  </SelectTrigger>
                  <SelectContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100">
                    {f.options.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)} className="focus:bg-[#17233B]">{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "document" ? (
                <DocumentField value={values[f.name]} onChange={(id) => set(f.name, id)} testId={`field-${f.name}`} />
              ) : f.type === "textarea" ? (
                <Textarea value={values[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder}
                  className={inputCls} data-testid={`field-${f.name}`} />
              ) : f.type === "switch" ? (
                <div className="flex items-center justify-between rounded-lg border border-[#2D3F62] bg-[#0B1120] px-3 py-2.5">
                  <span className="text-sm text-slate-300">{f.label}</span>
                  <Switch checked={!!values[f.name]} onCheckedChange={(v) => set(f.name, v)} data-testid={`field-${f.name}`} />
                </div>
              ) : (
                <Input type={f.type || "text"} value={values[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)}
                  placeholder={f.placeholder} step={f.step} className={inputCls} data-testid={`field-${f.name}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400" data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
            className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500"
            data-testid={`${testId}-submit`}>
            {saving ? "Saving…" : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
